import { Component } from 'react';
import { LoggerProvider } from 'utils';
import { logClientMessage } from '../../api/logger';
import { GenericErrorPage } from '../generic-error-page';
import type { EmbeddedErrorTrackingProps } from './embedded-error-tracking.interface';

interface ErrorBounderyState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export const logError = async (error: unknown) => {
  let value;

  if (error instanceof Error) {
    value = error.message;
  } else if (typeof error === 'string') {
    value = error;
  }

  if (value) {
    await logClientMessage({ value });
  }
};

export const logInfo = async (message: string) => {
  await logClientMessage({ value: message, type: 'info' });
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBounderyState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  async componentDidCatch(error: Error) {
    await logError(error);
  }

  render() {
    const { hasError } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }
      return null;
    }

    return children;
  }
}

export const EmbeddedErrorTracking = ({
  children,
}: EmbeddedErrorTrackingProps) => (
  <ErrorBoundary fallback={<GenericErrorPage />}>
    <LoggerProvider logError={logError} logInfo={logInfo}>
      {children}
    </LoggerProvider>
  </ErrorBoundary>
);
